import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DatabasesTab, DbUsersTab, CronTab, PanelUsersTab } from "./TenantResourcesTabs";
import { GrantsTab, SftpTab, ProcessesTab, UsageTab } from "./TenantOpsTabs";
import { useParams, useNavigate } from "react-router";
import {
  ActionBar,
  DataDetail,
  DataList,
  PageHeader,
  RelatedLinks,
  StatusPill,
  useActionRunner,
  type DataListColumn,
  NumberField,
  AccountTimeZonePanel,
  MailWebhooks,
  AccountApiTokens,
} from "@ozpanel/kit";
import { Button, StateView, formatDateTime, useAuth, useToast } from "@ozpanel/ui";
import { useAccountsApi, type LimitField, type LimitValue, type FeatureValue, type Tenant } from "../../accountsApi";
import { tenantActions } from "./tenantActions";

const limitColumns: DataListColumn<LimitValue>[] = [
  { key: "label", label: "Limit", sortable: true },
  {
    key: "value",
    label: "Value",
    sortable: true,
    value: (l) => l.value,
    // Unset is not 0: it is what the limit's own definition says (no limit, or — for Redis memory — no Redis).
    render: (l) => (l.value > 0 ? `${l.value}${l.unit ? " " + l.unit : ""}` : l.unset || "no limit"),
  },
  {
    key: "source",
    label: "Source",
    sortable: true,
    // The Interlock rule: a limit's number means nothing without what sets it. "unset" is a
    // materially different state from a plan-derived value and must not read the same.
    render: (l) => (
      <StatusPill tone={l.source === "unset" ? "neutral" : "ok"}>
        {l.source === "plan" && l.plan ? `plan: ${l.plan}` : l.source}
      </StatusPill>
    ),
  },
];

const featureColumns: DataListColumn<FeatureValue>[] = [
  { key: "label", label: "Feature", sortable: true },
  {
    key: "on",
    label: "On",
    value: (f) => (f.on ? "yes" : "no"),
    render: (f) => <StatusPill tone={f.on ? "ok" : "neutral"}>{f.on ? "on" : "off"}</StatusPill>,
  },
  { key: "source", label: "Source", sortable: true },
];

/** Per-account limit overrides.
 *
 * An override is DELIBERATELY sparse. A key that is absent falls back to the plan, so writing
 * every field would convert the whole set into overrides and the account would silently stop
 * following its plan — the same trap setTenantFeatures documents for capabilities. Only boxes the
 * operator actually filled are sent, and clearing one removes the override rather than setting
 * zero: zero is a real limit meaning "none allowed", which is not what an empty box means.
 */
function LimitOverrides({
  tenantId,
  fields,
  overrides,
  onSaved,
}: {
  tenantId: number;
  fields: LimitField[];
  overrides: Record<string, number>;
  onSaved: () => void;
}) {
  const api = useAccountsApi();
  const { notify } = useToast();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(overrides ?? {})) out[k] = String(v);
    return out;
  });
  const [busy, setBusy] = React.useState(false);

  if (!open) {
    return (
      <div className="mt-3">
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
          Override limits for this account
        </Button>
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    try {
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(draft)) {
        const t = v.trim();
        if (t === "") continue; // absent = follow the plan
        const n = Number(t);
        if (Number.isFinite(n)) out[k] = n;
      }
      await api.setTenantLimits(tenantId, out);
      notify({ kind: "success", title: "Limits applied" });
      setOpen(false);
      onSaved();
    } catch (e) {
      notify({ kind: "error", title: "Could not apply them", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const groups = Array.from(new Set(fields.map((f) => f.group || "Other")));
  return (
    <div className="mt-3 rounded-lg border border-line p-3">
      <p className="mb-2 max-w-3xl text-[12px] leading-relaxed text-muted">
        An empty box follows the plan. A number here overrides it for this account only, and stays
        put when the plan changes.
      </p>
      {groups.map((g) => (
        <div key={g} className="mb-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted">{g}</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {fields
              .filter((f) => (f.group || "Other") === g)
              .map((f) => (
                <NumberField
                  key={f.key}
                  label={f.label}
                  unit={f.unit}
                  value={draft[f.key] ?? ""}
                  placeholder={f.unset ? `follow the plan (unset: ${f.unset})` : "follow the plan"}
                  onChange={(v) => setDraft({ ...draft, [f.key]: v })}
                  help={f.detail}
                />
              ))}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => void save()} disabled={busy}>
          {busy ? "Applying…" : "Apply overrides"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function TenantDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const tenantId = Number(id);
  const api = useAccountsApi();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const runner = useActionRunner<Tenant>();

  const tenant = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => api.getTenant(tenantId),
    enabled: !!tenantId,
  });
  const limits = useQuery({
    queryKey: ["tenant-limits", tenantId],
    queryFn: () => api.getTenantLimits(tenantId),
    enabled: !!tenantId,
  });

  const actions = tenantActions(api, {
    loadTeardown: api.teardownPreview,
    onDeleted: () => navigate("/tenants"),
    onImpersonated: () => void refresh(),
  });

  if (tenant.isLoading || tenant.isError || !tenant.data) {
    return (
      <div>
        <StateView loading={tenant.isLoading} error={tenant.error} errorContext="this tenant" />
      </div>
    );
  }

  const t = tenant.data;

  return (
    <div>
      <PageHeader
        crumbs={[{ label: "Tenants", to: "/tenants" }, { label: t.name }]}
        title={t.name}
        status={<StatusPill tone={t.status === "active" ? "ok" : "warn"}>{t.status}</StatusPill>}
        subtitle={t.contact_email || undefined}
        actions={<ActionBar actions={actions} obj={t} runner={runner} />}
      />

      <DataDetail
        fields={[
          { label: "Contact name", value: t.contact_name || "—" },
          // An empty system_user is a real, visible state — a tenant that was never fully
          // provisioned (the same NULL that used to 500 the whole list).
          {
            label: "System user",
            value: t.system_user || <span className="text-warn">not provisioned</span>,
          },
          { label: "Region policy", value: t.region_policy },
          { label: "Cluster mode", value: t.cluster_mode },
          { label: "Created", value: formatDateTime(t.created_at) },
        ]}
      />

      <div className="mt-8">
        <AccountTimeZonePanel tenantId={tenantId} />
      </div>

      <section className="mt-8">
        <h3 className="eyebrow mb-2.5 text-accent2">Limits</h3>
        <DataList
          items={limits.data?.effective ?? []}
          columns={limitColumns}
          getRowKey={(l) => l.key}
          searchable
          searchPlaceholder="Search limits…" loading={limits.isLoading}
          error={limits.error}
          errorContext="limits" emptyMessage="No limits configured." />
        {/* The limits were readable and not settable: PUT /tenants/{id}/limits was reached by no
            console, so a quota could be seen and only changed by moving the whole account to a
            different plan — or with SQL. */}
        <LimitOverrides
          tenantId={tenantId}
          fields={limits.data?.fields ?? []}
          overrides={limits.data?.overrides ?? {}}
          onSaved={() => void limits.refetch()}
        />
      </section>

      <TenantPlanAndClustering tenant={t} tenantId={tenantId} onChanged={() => void refresh()} />

      <section className="mt-8">
        <h3 className="eyebrow mb-2.5 text-accent2">Features</h3>
        <DataList
          items={limits.data?.features ?? []}
          columns={featureColumns}
          getRowKey={(f) => f.key}
          loading={limits.isLoading}
          error={limits.error}
          errorContext="features" emptyMessage="No features." />
      </section>

      {/* Per-tenant resources. These endpoints existed with no v2 surface at all, so an
          operator could see an account's limits but not its databases, users or cron. */}
      <div className="mt-8 space-y-6">
        <DatabasesTab tenantId={String(t.id)} />
        <DbUsersTab tenantId={String(t.id)} />
        <GrantsTab tenantId={String(t.id)} />
        <CronTab tenantId={String(t.id)} />
        <MailWebhooks tenantId={String(t.id)} admin />
        <PanelUsersTab tenantId={String(t.id)} />
        <AccountApiTokens tenantId={t.id} />
        <SftpTab tenantId={String(t.id)} />
        <UsageTab tenantId={String(t.id)} />
        <ProcessesTab tenantId={String(t.id)} />
      </div>

      <RelatedLinks
        links={[
          { label: "App backends", to: "/apps", hint: "for this tenant" },
          { label: "Backups", to: `/modules/backups?tenant=${t.id}` },
          { label: "Usage & quotas", to: "/modules/usage" },
        ]}
      />
      {runner.dialog}
    </div>
  );
}

// Plan, capability overrides and clustering — the three PUTs that define what an account may do
// and where its things run. All were reachable only by curl.
function TenantPlanAndClustering({
  tenant, tenantId, onChanged,
}: {
  tenant: Tenant;
  tenantId: number;
  onChanged: () => void;
}) {
  const api = useAccountsApi();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [planId, setPlanId] = React.useState<string>("");
  const [mode, setMode] = React.useState<string>(tenant.cluster_mode || "local");
  const [targets, setTargets] = React.useState<string>("");

  const plans = useQuery({ queryKey: ["plans"], queryFn: () => api.listPlans(), retry: false });
  const limits = useQuery({ queryKey: ["tenant-limits", tenantId], queryFn: () => api.getTenantLimits(tenantId), enabled: !!tenantId });

  React.useEffect(() => { setPlanId(tenant.plan_id ? String(tenant.plan_id) : "0"); }, [tenant.plan_id]);

  const done = (title: string) => {
    notify({ kind: "success", title });
    void qc.invalidateQueries({ queryKey: ["tenant-limits", tenantId] });
    onChanged();
  };
  const fail = (e: unknown) => notify({ kind: "error", title: "That did not work", body: e instanceof Error ? e.message : String(e) });

  // A feature is toggled by sending ONLY that key. The map is partial by design: including every
  // key would turn every plan-derived value into an override in one click, and the account would
  // stop following its plan without anyone asking for that.
  const toggleFeature = (key: string, on: boolean) =>
    api.setTenantFeatures(tenantId, { [key]: on }).then(() => done(on ? `${key} enabled` : `${key} disabled`)).catch(fail);

  const overridden = (limits.data?.features ?? []).filter((f) => f.source !== "plan");

  return (
    <section className="mt-8">
      <h3 className="eyebrow mb-2.5 text-accent2">Plan &amp; placement</h3>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-line p-4">
          <div className="mb-2 text-[13px]">Plan</div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={planId} onChange={(e) => setPlanId(e.target.value)}
              className="min-w-[12rem] rounded-lg border border-line2 bg-bg px-3 py-1.5 text-[13px] text-fg">
              <option value="0">no plan</option>
              {(plans.data?.items ?? []).map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </select>
            <Button size="sm" onClick={() => void api.setTenantPlan(tenantId, Number(planId)).then(() => done("Plan changed")).catch(fail)}>
              Apply
            </Button>
          </div>
          <p className="mt-2 text-[11.5px] leading-relaxed text-muted">
            Limits and capabilities follow the plan unless a value has been overridden for this
            account. Removing the plan leaves only the overrides.
            {overridden.length > 0 && (
              <> {overridden.length} capability override{overridden.length === 1 ? "" : "s"} currently
              ignore the plan.</>
            )}
          </p>
        </div>

        <div className="rounded-lg border border-line p-4">
          <div className="mb-2 text-[13px]">Where this account&apos;s things run</div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={mode} onChange={(e) => setMode(e.target.value)}
              className="rounded-lg border border-line2 bg-bg px-3 py-1.5 text-[13px] text-fg">
              <option value="local">this node only</option>
              <option value="all">every node</option>
              <option value="region">a region</option>
              <option value="nodes">named nodes</option>
            </select>
            {(mode === "region" || mode === "nodes") && (
              <input value={targets} onChange={(e) => setTargets(e.target.value)}
                placeholder={mode === "nodes" ? "node10, node11" : "eu"}
                className="min-w-[10rem] flex-1 rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg" />
            )}
            <Button size="sm"
              onClick={() => void api.setTenantClustering(tenantId, mode, targets.split(",").map((x) => x.trim()).filter(Boolean))
                .then(() => done("Placement policy saved")).catch(fail)}>
              Apply
            </Button>
          </div>
          <p className="mt-2 text-[11.5px] leading-relaxed text-muted">
            This is the DEFINITION point for the account. It sets the default an app inherits when it
            has no placement of its own — an app already pinned to specific nodes keeps that pin.
          </p>
        </div>
      </div>

      {(limits.data?.features ?? []).length > 0 && (
        <div className="mt-4 rounded-lg border border-line p-4">
          <div className="mb-2 text-[13px]">Capabilities</div>
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {(limits.data?.features ?? []).map((f) => (
              <label key={f.key} className="flex items-center justify-between gap-3 border-b border-line py-1.5 text-[13px]">
                <span>
                  {f.label}
                  {/* Where the value comes from. Without it, turning something on that the plan
                      already grants silently converts it into an override that stops tracking the
                      plan. */}
                  <span className="ml-2 text-[11px] text-muted">{f.source === "plan" ? "from the plan" : "overridden"}</span>
                </span>
                <input type="checkbox" checked={!!f.on} onChange={(e) => void toggleFeature(f.key, e.target.checked)} />
              </label>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
